import Interactable from '../../Component/Interactable';
import SpriteComponent from '../../Component/Render/Sprite';
import TransformComponent from '../../Component/Transform';
import ChestComponent from '../../Component/World/Chest';
import Game from '../../Game';
import GameObject from '../../GameObject/GameObject';
import { GameObjectEvent } from '../../GameObject/GameObjectEvent';
import Animator from '../../Prototype/Animator';
import Container from '../../Prototype/Container';
import Chest from '../../Prototype/World/Chest';
import Fox, { FoxState } from '../../Prototype/World/Fox';
import FriendIcon from '../../Prototype/World/FriendIcon';
import Gate from '../../Prototype/World/Gate';
import LevelNode, { LevelNodeState } from '../../Prototype/World/LevelNode';
import PlayerIcon from '../../Prototype/World/PlayerIcon';
import Suitcase from '../../Prototype/World/Suitcase';
import WorldMap from '../../Prototype/World/WorldMap';
import { GameActions } from '../../State/Game/GameActions';
import { IGameState } from '../../State/Game/IGameState';
import { PlayerActions } from '../../State/Player/PlayerActions';
import {
    IChestUnlock,
    UnlockType,
    IPostcardUnlock,
} from '../../State/Player/IPlayerState';
import { PlayerTask } from '../../State/Player/PlayerTasks';
import StateManager from '../../State/StateManager';
import { UIActions } from '../../State/UI/UIActions';
import Point from '../../Utility/Point';
import { IScoreEntry } from '../../Utility/SDK/ISDKDriver';
import { delay } from '../../Utility/Task/TaskQueue';
import { FBTrackingEvent } from '../../Utility/Tracking/FBTrackingEvent';
import { StartAnimation, StopAnimation } from '../Animation/AnimationHelper';
import { AUDIO_WORLD } from '../Audio';
import { AudioTrackType } from '../Audio/AudioClip';
import { StartAudio } from '../Audio/AudioHelper';
import PIXIRenderer from '../Render/PIXIRenderer';
import { WORLD_WIDTH } from '../Render/PIXIRenderer';
import RenderSystem, { RenderSystemEvent } from '../Render/RenderSystem';
import System from '../System';
import LeaderboardHelper, { Leaderboard } from '../UI/LeaderboardHelper';
import FoxHandler from './Handler/FoxHandler';
import RewardSyncTask from './Task/RewardSyncTask';
import { WORLDS } from '.';
import { ResourceDriver } from '../../Utility/Resource/ResourceManager';
import PostcardPin, {
    PostcardPinState,
} from '../../Prototype/World/PostcardPin';
import PostcardPinComponent from '../../Component/World/PostcardPin';
import { IWinCriteriaData } from '../../State/Level/ILevelState';

export enum WorldComponent {
    GATE = 'gate',
    CHEST = 'chest',
    FOXY = 'foxy',
    POSTCARD_PIN = 'postcardPin',
}

export interface IWorldComponent {
    id: string;
    type: WorldComponent;
    [key: string]: any;
}

export default class WorldSystem extends System {
    public worldNumber: number;
    public worldData: {
        id: string;
        settings: any;
        tiles: string[];
        components: IWorldComponent[];
        levels: any[];
    };
    public worldMap: WorldMap;
    public worldMapComponents: Container;
    public worldAudioClip: string;

    public levelContainer: Container;
    public levelNodes: Map<number, LevelNode>;
    public playerIcon: PlayerIcon;
    public animator: Animator;

    private currentLevelNumber: number;
    private currentLevelPath: string;
    private currentLevelGoals: IWinCriteriaData[];

    private foxHandler: FoxHandler;

    private autoShowLevelMenuEnabled: boolean;
    private autoShowMenuRoutine: any;

    constructor(state: StateManager, worldNumber: number) {
        super(state);

        this.worldNumber = worldNumber;
    }

    public init(): void {
        this.levelNodes = new Map<number, LevelNode>();
    }

    public async preload(): Promise<any> {
        // Load world JSON which contains world image paths.
        Game.resource.add(
            `worldData${this.worldNumber}`,
            `resource/data/world/world_${this.worldNumber}.json`
        );

        await Game.resource.load();

        this.worldData = Game.resource.get<any>(`worldData${this.worldNumber}`);

        this.worldData.tiles.forEach((tilePath: string, index: number) => {
            const key: string = `world${this.worldNumber}_tile${index}`;
            if (!Game.resource.exists(key)) {
                Game.resource.add(
                    key,
                    `resource/graphics/world_tiles/${tilePath}`
                );
            }
        });

        this.worldAudioClip = AUDIO_WORLD[this.worldNumber - 1];

        // World UI.
        Game.resource.add('worldHUD', 'resource/data/ui/worldHUD.json');
        Game.resource.add('sideMenu', 'resource/data/ui/sideMenu.json');
        Game.resource.add('levelMenu', 'resource/data/ui/levelMenu.json');
        Game.resource.add('travelMenu', 'resource/data/ui/travelMenu.json');
        Game.resource.add('wheelMenu', 'resource/data/ui/wheelMenu.json');
        Game.resource.add('noLivesMenu', 'resource/data/ui/noLivesMenu.json');
        Game.resource.add(
            'receiveLivesMenu',
            'resource/data/ui/receiveLivesMenu.json'
        );
        Game.resource.add(
            'sendLivesMenu',
            'resource/data/ui/sendLivesMenu.json'
        );
        Game.resource.add(
            'receiveLivesFriendMenu',
            'resource/data/ui/receiveLivesFriendMenu.json'
        );
        Game.resource.add(
            'receiveCoinsMenu',
            'resource/data/ui/receiveCoinsMenu.json'
        );

        Game.resource.add(
            'receiveCoinsFriendMenu',
            'resource/data/ui/receiveCoinsFriendMenu.json'
        );

        Game.resource.add(
            'receiveMissionMenu',
            'resource/data/ui/receiveMissionMenu.json'
        );
        Game.resource.add(
            'worldUnlockMenu',
            'resource/data/ui/worldUnlockMenu.json'
        );

        Game.resource.add(
            'scoreBeatenMenu',
            'resource/data/ui/scoreBeatenMenu.json'
        );

        Game.resource.add(
            'threeStarRewardMenu',
            'resource/data/ui/threeStarRewardMenu.json'
        );

        Game.resource.add('rateAppMenu', 'resource/data/ui/rateAppMenu.json');

        Game.resource.add(
            'resolveDataConflictMenu',
            'resource/data/ui/ResolveDataConflictMenu.json'
        );
        // Spin wheel texture.
        Game.resource.add('new_wheel', 'resource/graphics/other/new_wheel.png');

        // Textures.
        Game.resource.add(
            'atlas_world',
            'resource/graphics/export/atlas_world.json'
        );
        Game.resource.add(
            'atlas_ui_world',
            'resource/graphics/export/atlas_ui_world.json'
        );
        Game.resource.add(
            'atlas_ui_common',
            'resource/graphics/export/atlas_ui_common.json'
        );
        Game.resource.add('atlas_hd', 'resource/graphics/export/atlas_hd.json');
        Game.resource.add(
            'atlas_ui_platform',
            'resource/graphics/export/atlas_ui_platform.json'
        );

        // Dragonbones Currency Rewards VFX.
        Game.resource.add(
            'CurrencyRewards_skeleton_data',
            'resource/dragonbones/reward/skeleton.json'
        );
        Game.resource.add(
            'CurrencyRewards_texture',
            'resource/dragonbones/reward/texture.png'
        );
        Game.resource.add(
            'CurrencyRewards_texture_data',
            'resource/dragonbones/reward/texture.json'
        );

        await Game.resource.load();
    }

    public onUpdate(): void {
        const nodePadding = [70, 90, 80, 90];
        this.levelNodes.forEach(node => {
            const position = node.container.toGlobal(new Point());

            if (
                position.y + nodePadding[0] < 0 ||
                position.x - nodePadding[1] > PIXIRenderer.renderer.width ||
                position.y - nodePadding[2] > PIXIRenderer.renderer.height ||
                position.x + nodePadding[3] < 0
            ) {
                node.container.visible = false;
            } else {
                node.container.visible = true;
            }
        });
    }

    public onStart(): void {
        this.createWorld();

        this.autoShowLevelMenuEnabled = true;
        this.worldMap.container.once('pointerdown', () => {
            this.stopShowMenuCoroutine();
            this.autoShowLevelMenuEnabled = false;
        });
        this.startShowLevelMenuCoroutine();
    }

    public onDestroy(): void {
        RenderSystem.off(RenderSystemEvent.RESIZE);
        this.foxHandler.stopFoxTimerInterval();
        this.stopShowMenuCoroutine();
    }

    public createWorld(): void {
        // Find current level node position.
        // World may contain less than 60 (default) levels.
        const highestWorldLevelNumber = Math.max(
            ...this.worldData.levels.map((level: any) => level.number)
        );
        this.currentLevelNumber = Math.min(
            Game.player.getCurrentWorldLevel(this.worldNumber),
            highestWorldLevelNumber
        );

        const levelData: any = this.worldData.levels.find(
            (lvl: any) => lvl.number === this.currentLevelNumber
        );

        this.currentLevelPath = levelData.path;
        this.currentLevelGoals = levelData.goals;

        this.worldMap = GameObject.Instantiate(WorldMap, {
            // Background tiles.
            backgrounds: this.worldData.tiles
                .map((value: string, index: number) => {
                    return `world${this.worldNumber}_tile${index}`;
                })
                .reverse(),

            buttonMode: false,
        });
        this.scene.addGameObject(this.worldMap);

        this.levelContainer = GameObject.Instantiate(Container);
        this.worldMap.addChild(this.levelContainer);

        this.worldMapComponents = GameObject.Instantiate(Container);
        this.worldMap.addChild(this.worldMapComponents);

        this.animator = GameObject.Instantiate(Animator);
        this.scene.addGameObject(this.animator);

        this.addLevelNodes();
        this.addMapComponents();
        this.addPlayerIcon();
        this.addFriendIcons();
        this.addSuitcase();

        this.resizeWorld();
        RenderSystem.on(RenderSystemEvent.RESIZE, this.resizeWorld.bind(this));

        // Load and start world audio asynchronously.
        Game.resource.add(this.worldAudioClip, null, ResourceDriver.SECONDARY);
        Game.resource.wait(this.worldAudioClip).then(() => {
            StartAudio(this.scene, this.worldAudioClip, AudioTrackType.MUSIC, {
                loop: true,
            });
        });
    }

    public addLevelNodes(): void {
        this.worldData.levels.forEach((levelData: any) => {
            const levelNumber: number = Number(levelData.number);
            const levelState: LevelNodeState = this.getLevelState(levelNumber);
            const levelNode: LevelNode = GameObject.Instantiate(LevelNode, {
                x: levelData.x,
                y: levelData.y,
                levelNumber: levelNumber,
                levelState: levelState,
                stars: Game.player.getLevelStars(levelNumber),
            });
            this.levelNodes.set(levelNumber, levelNode);
            this.levelContainer.addChild(levelNode);

            if (levelState !== LevelNodeState.FUTURE) {
                levelNode.on(GameObjectEvent.POINTER_DOWN, () => {
                    StartAnimation(levelNode, 'NodePress');
                });
                levelNode.on(GameObjectEvent.POINTER_UP, () => {
                    StartAnimation(levelNode, 'NodeRelease');
                });
                levelNode.on(GameObjectEvent.POINTER_OUT, () => {
                    StartAnimation(levelNode, 'NodeRelease');
                });
                levelNode.on(GameObjectEvent.SELECTED, () => {
                    this.state.dispatch(
                        UIActions.showPopup('levelMenu', {
                            level: levelNumber,
                            path: levelData.path,
                            goals: levelData.goals,
                        })
                    );
                });
            }
        });
    }

    public addMapComponents(): void {
        const unlockedChests: IChestUnlock[] = Game.state
            .getState()
            .playerState.playerUnlocks.filter(
                (unlock): unlock is IChestUnlock =>
                    unlock.type === UnlockType.CHEST
            );

        const unlockedPostcards: IPostcardUnlock[] = Game.state
            .getState()
            .playerState.playerUnlocks.filter(
                (unlock): unlock is IPostcardUnlock =>
                    unlock.type === UnlockType.POSTCARD
            );

        this.worldData.components.forEach(componentData => {
            switch (componentData.type) {
                case WorldComponent.CHEST:
                    // prettier-ignore
                    if (unlockedChests.find(unlock => unlock.id === componentData.id)) {
                        return;
                    }

                    const chest: Chest = GameObject.Instantiate(Chest, {
                        id: componentData.id,
                        x: componentData.x,
                        y: componentData.y,
                        totalStars: Game.player.getTotalStars(),
                        requiredStars: componentData.requiredStars,
                    });
                    this.worldMapComponents.addChild(chest);

                    chest.on(GameObjectEvent.SELECTED, () => {
                        this.onChestSelect(chest);
                    });
                    break;
                case WorldComponent.GATE:
                    if (WORLDS.length > this.worldNumber) {
                        const gate: Gate = GameObject.Instantiate(Gate, {
                            x: componentData.x,
                            y: componentData.y,
                            open: this.isGateOpen(),
                        });
                        this.levelContainer.addChild(gate);

                        gate.on(GameObjectEvent.SELECTED, () => {
                            this.onGateSelect(gate);
                        });
                    }
                    break;
                case WorldComponent.FOXY:
                    const fox: Fox = GameObject.Instantiate(Fox);
                    this.worldMapComponents.addChild(fox);

                    this.foxHandler = new FoxHandler(fox);
                    this.foxHandler.setFoxPosition(componentData);
                    this.foxHandler.startFoxTimerInterval();
                    this.startCoroutine(this.foxHandler.speak());

                    fox.on(GameObjectEvent.SELECTED, () => {
                        this.onFoxSelect();
                    });
                    break;
                case WorldComponent.POSTCARD_PIN:
                    if (!Game.sdk.driver.hasSocialFeaturesSupport()) {
                        break;
                    }

                    // prettier-ignore
                    const id = `postcard_${this.worldNumber}_${componentData.place}`;

                    // prettier-ignore
                    const firstWorldLevel = Game.player.getFirstWorldLevel(this.worldNumber);
                    // prettier-ignore
                    const requiredLevel = firstWorldLevel + componentData.place * 20 - 1;

                    let state = PostcardPinState.AVAILABLE;
                    if (!unlockedPostcards.some(unlock => unlock.id === id)) {
                        state =
                            Game.player.getHighestLevelNumber() > requiredLevel
                                ? PostcardPinState.READY
                                : PostcardPinState.UNKNOW;
                    }
                    const pin: PostcardPin = GameObject.Instantiate(
                        PostcardPin,
                        {
                            id: id,
                            state: state,
                            requiredLevel: requiredLevel,
                            ...componentData,
                        }
                    );
                    this.worldMapComponents.addChild(pin);

                    pin.on(GameObjectEvent.SELECTED, () => {
                        this.onPostcardPinSelect(pin);
                    });
                    break;
            }
        });
    }

    public addSuitcase(): void {
        if (
            Game.state
                .getState()
                .playerState.playerUnlocks.find(
                    unlock => unlock.type === UnlockType.SUITCASE
                )
        )
            return;

        // this is a fixed that was applied to reposition the suitcase where it
        // does not cover anything, since for the web version of SOLI we started
        // with paris as the first world
        const parisOffset = { x: -250, y: -280 };

        const suitcase: Suitcase = GameObject.Instantiate(Suitcase, {
            x: 1920 / 2 + 540 + parisOffset.x,
            y: 1080 * 4 - 1150 + parisOffset.y,
        });
        this.worldMapComponents.addChild(suitcase);
    }

    public addPlayerIcon(): void {
        const currentNode: LevelNode = this.getCurrentLevelNode();
        const previousNode = this.levelNodes.get(this.currentLevelNumber - 1);
        const currentLevelNodeTransform: TransformComponent = currentNode.getComponent(
            TransformComponent
        );

        // Check if the player should be positioned on the previous node (e.g. the player
        // just completed a level and the icon will jump to the next node).
        const position: Point = currentLevelNodeTransform.position.clone() as Point;
        if (
            Game.state
                .getState()
                .playerState.tasks.find(
                    t => t.task === PlayerTask.LEVEL_NODE_COMPLETE
                ) &&
            currentNode &&
            previousNode &&
            currentNode !== previousNode &&
            Game.player.getHighestLevelNumber() === this.getCurrentLevelNumber()
        ) {
            position.copy(
                previousNode.getComponent(TransformComponent).position as Point
            );
        }

        this.playerIcon = GameObject.Instantiate(PlayerIcon, {
            position: position,
        });

        const avatarURL = Game.sdk.getAvatar();

        if (avatarURL) {
            LeaderboardHelper.setPlayerAvatar(
                'player_avatar',
                avatarURL,
                this.playerIcon
                    .getComponentsInChildren(SpriteComponent)
                    .find(s => s.texture === 'no_avatar')
            );
        }

        this.playerIcon.on(GameObjectEvent.SELECTED, () => {
            this.state.dispatch(
                UIActions.showPopup('levelMenu', {
                    level: this.currentLevelNumber,
                    path: this.currentLevelPath,
                    goals: this.currentLevelGoals,
                })
            );
        });

        this.worldMapComponents.addChild(this.playerIcon);
    }

    public async addFriendIcons() {
        const entries: IScoreEntry[] = await Game.sdk.getFriendScores(
            Leaderboard.TOTAL_LEVELS,
            'default'
        );

        if (!this.worldMap || this.worldMap.destroyed) return;

        for (let i = 0, n = entries.length; i < n; i++) {
            const entry: IScoreEntry = entries[i];
            if (entry.user.playerId === Game.sdk.getUserId()) continue;

            const levelNode: LevelNode = this.getLevelNode(entry.score);
            if (levelNode) {
                const levelNodeTransform: TransformComponent = levelNode.getComponent(
                    TransformComponent
                );
                if (!levelNodeTransform) continue;

                const friendIcon = GameObject.Instantiate(FriendIcon, {
                    position: levelNodeTransform.position.clone(),
                });
                this.worldMapComponents.addChild(friendIcon);

                const avatarId = LeaderboardHelper.getPlayerAvatarId(
                    entry.user.playerId,
                    entry.user.isCurrentUser
                );

                LeaderboardHelper.setPlayerAvatar(
                    avatarId,
                    entry.user.avatarUrl,
                    friendIcon
                        .getComponentsInChildren(SpriteComponent)
                        .find(s => s.texture === 'no_avatar')
                );
            }
        }

        // Bring player icon to the front of friends icons.
        this.scene.emit(
            RenderSystemEvent.SORT_CHILDREN,
            this.worldMapComponents
        );
    }

    public handleStateChanged(state: IGameState): void {
        this.taskQueue.queue(new RewardSyncTask());

        if (
            state.UIState.popupID === null ||
            state.UIStatePrio.popupIDPrio === null ||
            state.UIState.popupID === 'sideMenu'
        ) {
            this.taskQueue.start(this);
        }

        // TODO: Move it to a priority task queue.
        this.stopShowMenuCoroutine();
        if (this.autoShowLevelMenuEnabled) {
            if (
                state.previousState.UIState.popupID !== 'levelMenu' &&
                state.UIState.popupID === null &&
                state.UIStatePrio.popupIDPrio === null
            ) {
                this.startShowLevelMenuCoroutine();
            } else if (this.taskQueue.tasks.length === 0) {
                // Should not open if another pop-up is open.
                this.autoShowLevelMenuEnabled = false;
            }
        }

        if (
            state.previousState.UIState.popupID === 'wheelMenu' &&
            state.UIState.popupID === null &&
            state.playerState.tasks.length > 0 &&
            (state.playerState.wheelSpins > 0 || state.platform.adsRewarded)
        ) {
            this.stopShowMenuCoroutine();
            this.autoShowMenuRoutine = this.startCoroutine(
                this.showWheelMenu()
            );
        }
    }

    public getCurrentLevelNumber(): number {
        return this.currentLevelNumber;
    }

    public getCurrentLevelPath(): string {
        return this.currentLevelPath;
    }

    public getCurrentLevelGoals(): IWinCriteriaData[] {
        return this.currentLevelGoals;
    }

    public getCurrentLevelNode(): LevelNode {
        return this.getLevelNode(this.currentLevelNumber);
    }

    private getLevelNode(level: number): LevelNode {
        return this.levelNodes.get(level);
    }

    private getLevelState(levelNumber: number): LevelNodeState {
        const currentLevel: number = Game.player.getHighestLevelNumber();

        if (currentLevel > levelNumber) return LevelNodeState.COMPLETE;
        if (currentLevel < levelNumber) return LevelNodeState.FUTURE;
        return LevelNodeState.CURRENT;
    }

    private isGateOpen(): boolean {
        const stars: number = Game.player.getLevelStars(
            this.currentLevelNumber
        );

        return stars > 0;
    }

    private onChestSelect(chest: Chest): void {
        const chestComponent = chest.getComponent(ChestComponent);
        if (chestComponent.requiredStars > Game.player.getTotalStars()) {
            StartAnimation(chest.find('speechBubble'), 'SpeechBubblePopUp');
        } else {
            chest.getComponent(Interactable).enabled = false;
            StartAnimation(chest.find('chestSprite'), 'ChestOpen');
            const shadowSprite = chest.find('shadowSprite');
            StopAnimation(shadowSprite);
            shadowSprite.getComponent(SpriteComponent).visible = true;
            shadowSprite.getComponent(SpriteComponent).alpha = 0.2;
            shadowSprite.getComponent(TransformComponent).scale.set(1, 0.5);

            this.state.dispatch(PlayerActions.unlockChest(chest.id));
        }
    }

    private onGateSelect(gate: Gate): void {
        if (this.isGateOpen()) {
            if (Game.state.getState().playerState.world > this.worldNumber) {
                Game.state.dispatch(
                    GameActions.loadWorld(this.worldNumber + 1)
                );
            } else {
                Game.state.dispatch(
                    UIActions.showPopup('worldUnlockMenu', {
                        requiredStars: this.worldData.settings.StargateStar,
                    })
                );
            }
        }
    }

    private onFoxSelect(): void {
        Game.sdk.gameTracking(FBTrackingEvent.fox_popup_show);
        // Receive a random gift: 50 coins, 75 coins or 1 life.
        this.state.dispatch(PlayerActions.receiveFoxReward());
        // Change the fox state.
        this.foxHandler.setFoxState(FoxState.SEARCHING);
    }

    private onPostcardPinSelect(pin: PostcardPin): void {
        const postcardPinComponent = pin.getComponent(PostcardPinComponent);
        if (postcardPinComponent.state === PostcardPinState.UNKNOW) {
            StartAnimation(pin.find('speechBubble'), 'SpeechBubblePopUp');
            return;
        }

        this.state.dispatch(
            UIActions.showPopup('postcardMenu', {
                background: postcardPinComponent.background,
                place: postcardPinComponent.place,
            })
        );

        if (postcardPinComponent.state === PostcardPinState.READY) {
            this.state.dispatch(PlayerActions.unlockPostcard(pin.id));
            pin.find('postcardPin/postcardCoins').destroy();
            postcardPinComponent.state = PostcardPinState.AVAILABLE;
        }
    }

    private startShowLevelMenuCoroutine(): void {
        this.stopShowMenuCoroutine();
        if (this.autoShowLevelMenuEnabled) {
            this.autoShowMenuRoutine = this.startCoroutine(
                this.showLevelMenu()
            );
        }
    }

    private stopShowMenuCoroutine(): void {
        if (this.autoShowMenuRoutine) {
            this.stopCoroutine(this.autoShowMenuRoutine);
            this.autoShowMenuRoutine = null;
        }
    }

    private *showLevelMenu(): IterableIterator<number> {
        while (this.taskQueue.isRunning()) yield;

        yield* delay(2500);

        this.autoShowMenuRoutine = null;

        if (this.autoShowLevelMenuEnabled) {
            this.autoShowLevelMenuEnabled = false;
            this.state.dispatch(
                UIActions.showPopup('levelMenu', {
                    level: this.getCurrentLevelNumber(),
                    path: this.getCurrentLevelPath(),
                    goals: this.getCurrentLevelGoals(),
                })
            );
        }
    }

    private *showWheelMenu(): IterableIterator<number> {
        while (this.taskQueue.isRunning()) yield;
        this.autoShowMenuRoutine = null;
        this.state.dispatch(UIActions.showPopup('wheelMenu', {}));
    }

    private resizeWorld(): void {
        const levelData: any = this.worldData.levels.find(
            (lvl: any) => lvl.number === this.currentLevelNumber
        );

        // const width = this.worldMap.container.width;
        // using a the fixed world width specified in the PIXIRenderer.ts
        // since the container width of worldMap jumps between 1280 and 1920
        const width = WORLD_WIDTH;
        const scale = PIXIRenderer.renderer.width / width;

        this.worldMap.container.scale.set(scale);
        this.worldMap.getComponent(TransformComponent).y =
            -levelData.y * scale + PIXIRenderer.renderer.height * 0.5;
    }

    protected onLateUpdate(): void {}

    protected onStop(): void {}
}
